<template>
  <div class="row">
    <div class="col-sm-2">
      <OverviewBox 
        title="Company Account"
        :loading="loading"
        :avatarImage="logoURL"
        :avatarName="accountData.companyName"
        :avatarCircleMode="false"
        :avatarEditable="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.account)"
        @editImage="editAvatarImage"
        @removeImage="removeAvatarImage" >

        <h4>{{accountData.companyName}}</h4>

        <OverviewBoxList>
          <OverviewBoxListItem title="Account Subscription">
            <span v-if="accountData.subscription.isActive" class="status-active">Active</span>
            <span v-if="!accountData.subscription.isActive" class="status-archive">Inactive</span>
          </OverviewBoxListItem>
        </OverviewBoxList>
      </OverviewBox>
    </div>
    <div class="col-sm-10">
      <TabbedBox :loading="loading">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-account-main">
            <i class="far fa-user"></i> Company Account
          </Tab>
          <Tab tabid="tab-account-locations">
            <i class="far fa-map-marker-alt"></i> Locations
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-account-main">
            <DataPageArea title="Details">
              <template slot="actionArea">
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.account)" class="float-right">
                  <DropdownMenuItem @click="editAccount" icon="fa-pencil">Edit Account</DropdownMenuItem>
                </ActionDropMenu>
              </template>
              <template slot="dataArea">
                <DataItem title="Company Name">{{accountData.companyName}}</DataItem>
                <DataItem title="Display Name">{{accountData.displayName}}</DataItem>
                <DataItem title="Account Currency">{{accountData.currencyCode}}</DataItem>
                <DataItem title="Sales Tax Rate">{{salesTaxRate}}</DataItem>
                <DataItem title="Account Timezone">{{accountData.timezoneCode}}</DataItem>
                <DataItem title="Email - Reply To">{{accountData.emailReplyToAddress}}</DataItem>
                <DataItem title="Email - From Address">{{accountData.emailFromName}}</DataItem>
                <DataItem title="ACN">{{accountData.companyACN}}</DataItem>
                <DataItem title="ABN">{{accountData.companyABN}}</DataItem>
                <DataItem title="Country Code">{{accountData.countryCode}}</DataItem>
                <DataItem title="State Code">{{accountData.stateCode}}</DataItem>                
              </template>
            </DataPageArea>
          </TabPage>

          <TabPage tabid="tab-account-locations">
            <div class="row">
              <div class="col-lg-12">
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.account)" class="float-right">
                  <DropdownMenuItem @click="addAccountLocation" icon="fa-pencil">Add Location</DropdownMenuItem>
                </ActionDropMenu>
                <h3>Location List</h3>
              </div>
            </div>
              
            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <th>Active</th>
                  <th>Address</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Fax</th>
                  <th>Labels</th>
                  <th>Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="location in accountData.accountLocations" :key="location.id">
                  <td class="font-weight-bold">{{location.name}}</td>
                  <td><IconBoolean :value="location.isActive" /></td>
                  <td>{{location.formattedAddress}}</td>
                  <td>{{location.contactEmail}}</td>
                  <td>{{location.contactPhone}}</td>
                  <td>{{location.contactFax}}</td>
                  <td><LabelTags :labelList="location.labelList" /></td>
                  <td>
                    <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.account)" :tableAction="true">
                      <DropdownMenuItem @click="editAccountLocation(location)" icon="fa-pencil">Edit Location</DropdownMenuItem>
                      <DropdownMenuItem @click="setAccountLocationActive(location, false)" icon="fa-times" v-if="location.isActive">Make Inactive</DropdownMenuItem>
                      <DropdownMenuItem @click="setAccountLocationActive(location, true)" icon="fa-check" v-if="!location.isActive">Make Active</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
            </StaticDataTable>
          </TabPage>
        </template>
      </TabbedBox>
    </div>
  </div>
</template>

<script>
import StaticDataTable from '@/components/table/staticDataTable.vue'

import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'
import AccountEditDialog from '@/modules/admin/views/accountEditDialog.vue'
import AccountLocationEditDialog from '@/modules/admin/views/accountLocationEditDialog.vue'

export default {
  name: "AccountAdmin",
  components: {
    StaticDataTable
  },
  data: () => {
    return {
      loading: true,
      accountData: {
        subscription: { isActive: false }
      }
    };
  },
  computed: {
    logoURL() {
      if(this.$data.loading)
        return ''
      if(this.$data.accountData.logoURL == null || this.$data.accountData.logoURL == '') {
        return '/glass/img/admin-overview-account.png'
      }
      return this.$data.accountData.logoURL
    },
    salesTaxRate() {
      if(this.$data.accountData.salesTaxRate == null)
        return null
      return this.$data.accountData.salesTaxRate.displayName
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true

      // Get the account details
      this.$apiService.adminService.getAccount().then(response => {
        this.$data.accountData = response.data;

        // Update store with account info
        this.$store.commit("account/setAccountData", response.data)
      })
      .finally(() => {
        this.loading = false
      })
    },
    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        APIresource: 'account',
        currentURL: this.$data.accountData.logoURL
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestData()

          // Refresh the profile so the sidebar image is updated
          this.$api.get("/profile").then(response => {
            this.$store.commit("profile/setProfile", response.data);
          })
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this user\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(null, 'account').then(response => {
            this.successToast('Logo Removed')
            this.requestData()

            // Refresh the profile so the sidebar image is updated
            this.$api.get("/profile").then(response => {
              this.$store.commit("profile/setProfile", response.data);
            })
          })
        }
      })
    },
    editAccount() {
      this.displayModal(AccountEditDialog, { }, this.$data.accountData)
      .then((success) => {
        if(success) {
          this.successToast('Account Updated')
          this.requestData()

          // Refresh the profile so the account name is updated on the sidebar
          this.$api.get("/profile").then(response => {
            this.$store.commit("profile/setProfile", response.data);
          })
        }
      })
    },
    addAccountLocation() {
      this.displayModal(AccountLocationEditDialog, {
        isCreateMode: true,
      })
      .then((success) => {
        if(success) {
          this.successToast('Location Added')
          this.requestData()
        }
      })
    },
    editAccountLocation(location) {
      this.displayModal(AccountLocationEditDialog, {
        isCreateMode: false,
      }, location)
      .then((success) => {
        if(success) {
          this.successToast('Location Updated')
          this.requestData()
        }
      })
    },
    setAccountLocationActive(location, isActive) {
      this.$apiService.adminService.updateAccountLocation(location.id, { isActive: isActive })
      .then(response => {
        this.successToast('Location Updated')
        this.requestData()
      })
    },
  }
}

</script>